import confetti from "canvas-confetti"

/** Gold, cream and the report's green: the colours the report is drawn in. */
export const GOLD_CONFETTI_COLOURS = ["#be8a2d", "#e7c675", "#f6e3b4", "#fbf1df", "#00634f"]

/**
 * A full screen canvas the confetti is drawn on, which takes no clicks and goes when it is done.
 * Confetti is drawn on a canvas of its own rather than the library's shared one so it can be
 * taken out of the page again, and so it sits over everything, pop ups included.
 */
function mountCanvas(options: confetti.GlobalOptions) {
    const canvas = document.createElement("canvas")
    canvas.setAttribute("aria-hidden", "true")
    canvas.style.position = "fixed"
    canvas.style.top = "0"
    canvas.style.left = "0"
    canvas.style.width = "100%"
    canvas.style.height = "100%"
    canvas.style.pointerEvents = "none"
    canvas.style.zIndex = "999999"
    document.body.appendChild(canvas)
    return {
        fire: confetti.create(canvas, { resize: true, ...options }),
        remove: () => canvas.parentNode?.removeChild(canvas),
    }
}

/** Two cannons, one at each side, for three seconds. */
export function fireSideCannons(durationMs = 3000) {
    const { fire, remove } = mountCanvas({})
    const end = Date.now() + durationMs
    const frame = () => {
        fire({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0, y: 0.7 } })
        fire({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1, y: 0.7 } })
        if (Date.now() < end) requestAnimationFrame(frame)
        else setTimeout(remove, 2000)
    }
    frame()
}

/**
 * A big gold celebration: a burst from the middle of the screen, then two more from either side,
 * then a shower. Nothing at all for somebody who has asked their device for less motion.
 */
export function fireGoldBurst() {
    const { fire, remove } = mountCanvas({ disableForReducedMotion: true })
    const base = { colors: GOLD_CONFETTI_COLOURS, ticks: 320, scalar: 1.4, gravity: 0.9 }
    const shots = [
        [0, { particleCount: 220, spread: 100, startVelocity: 62, origin: { x: 0.5, y: 0.6 } }],
        [180, { particleCount: 130, spread: 70, startVelocity: 58, angle: 60, origin: { x: 0, y: 0.7 } }],
        [180, { particleCount: 130, spread: 70, startVelocity: 58, angle: 120, origin: { x: 1, y: 0.7 } }],
        [420, { particleCount: 160, spread: 160, startVelocity: 35, decay: 0.92, origin: { x: 0.5, y: 0.3 } }],
    ] as const
    let last: unknown
    shots.forEach(([delay, options]) => setTimeout(() => { last = fire({ ...base, ...options }) }, delay))
    setTimeout(() => Promise.resolve(last).then(remove, remove), 500)
}

/** Fireworks: bursts at random points across the top of the screen for three seconds. */
export function fireFireworks(durationMs = 3000) {
    const { fire, remove } = mountCanvas({ disableForReducedMotion: true })
    const end = Date.now() + durationMs
    const burst = () => {
        const options = { particleCount: 60, spread: 360, startVelocity: 30, ticks: 70, zIndex: 0 }
        fire({ ...options, origin: { x: 0.1 + Math.random() * 0.3, y: Math.random() * 0.4 } })
        fire({ ...options, origin: { x: 0.6 + Math.random() * 0.3, y: Math.random() * 0.4 } })
        if (Date.now() < end) setTimeout(burst, 250)
        else setTimeout(remove, 2000)
    }
    burst()
}

const CELEBRATED_PREFIX = "reportCelebrated:"

/**
 * Whether this payment has been celebrated, and if not, that it now has been.
 *
 * Kept in the browser against the payment's id, so a page that is loaded again, or a page that
 * renders twice, does not celebrate the same payment twice.
 */
export function claimCelebration(paymentIntentId: string): boolean {
    const key = CELEBRATED_PREFIX + paymentIntentId
    try {
        if (localStorage.getItem(key)) return false
        localStorage.setItem(key, "true")
    } catch {
        // Without storage the page still celebrates, which is the better way to be wrong.
    }
    return true
}
